import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrashAlt, FaUsers, FaUser, FaCalendarAlt, FaChartBar, FaSearch, FaFilter, FaTimes } from 'react-icons/fa';
// Member가 아닌 RegisteredMember를 사용합니다.
import type { RegisteredMember } from '../../types/member';
import { POSITION_MAP, CELL_GROUP_MAP } from '../../utils/mappings'; // import 추가
import { Modal } from '../common/Modal'; // Modal 컴포넌트 import

interface MemberListProps {
  members: RegisteredMember[];
  onEdit?: (member: RegisteredMember) => void;
  onDelete?: (id: string) => void;
  isLoading?: boolean;
  error?: string | null;
}

interface CellGroupDetailModalProps {
  cellGroupId: string;
  isOpen: boolean;
  onClose: () => void;
  members: RegisteredMember[];
}

// 소그룹 상세 정보 모달 컴포넌트
export const CellGroupDetailModal: React.FC<CellGroupDetailModalProps> = React.memo(({
  cellGroupId,
  isOpen,
  onClose,
  members
}) => {
  const cellGroupName = CELL_GROUP_MAP[cellGroupId as keyof typeof CELL_GROUP_MAP] || cellGroupId;
  
  // 해당 소그룹에 속한 회원 필터링
  const cellGroupMembers = useMemo(() =>
    members.filter(member => member.cellGroup === cellGroupId),
    [members, cellGroupId]
  );
  
  // 소그룹 통계 계산
  const maleCount = useMemo(() =>
    cellGroupMembers.filter(m => m.gender === 'male').length,
    [cellGroupMembers]
  );
  const femaleCount = useMemo(() =>
    cellGroupMembers.filter(m => m.gender === 'female').length,
    [cellGroupMembers]
  );
  const totalCount = useMemo(() => cellGroupMembers.length, [cellGroupMembers]);
  
  // 직분별 통계
  const positionStats = useMemo(() =>
    cellGroupMembers.reduce((acc, member) => {
      const position = member.position || '미지정';
      acc[position] = (acc[position] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    [cellGroupMembers]
  );
  
  // 연령대 통계
  const ageStats = useMemo(() =>
    cellGroupMembers.reduce((acc, member) => {
      if (!member.dateOfBirth) return acc;
      
      const age = new Date().getFullYear() - new Date(member.dateOfBirth).getFullYear();
      let ageGroup = '미지정';
      if (age < 20) ageGroup = '10대 이하';
      else if (age < 30) ageGroup = '20대';
      else if (age < 40) ageGroup = '30대';
      else if (age < 50) ageGroup = '40대';
      else if (age < 60) ageGroup = '50대';
      else ageGroup = '60대 이상';
      
      acc[ageGroup] = (acc[ageGroup] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    [cellGroupMembers]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${cellGroupName} - 상세 정보`}>
      <div className="space-y-6">
        {/* 소그룹 개요 */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 flex items-center">
              <FaUsers className="mr-2" />
              소그룹 개요
            </h3>
            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium">
              총 {totalCount}명
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
              <FaUser className="mx-auto mb-2 text-blue-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400">남성</p>
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{maleCount}명</p>
            </div>
            <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
              <FaUser className="mx-auto mb-2 text-pink-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400">여성</p>
              <p className="text-xl font-bold text-pink-600 dark:text-pink-400">{femaleCount}명</p>
            </div>
            <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
              <FaChartBar className="mx-auto mb-2 text-green-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400">직분 수</p>
              <p className="text-xl font-bold text-green-600 dark:text-green-400">{Object.keys(positionStats).length}개</p>
            </div>
            <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
              <FaCalendarAlt className="mx-auto mb-2 text-purple-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400">평균 연령대</p>
              <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                {totalCount > 0 ? Object.keys(ageStats)[0] || 'N/A' : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* 직분별 분포 */}
        <div>
          <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <FaChartBar className="mr-2" />
            직분별 분포
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(positionStats).map(([position, count]) => (
              <div key={position} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {POSITION_MAP[position as keyof typeof POSITION_MAP] || position}
                </span>
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-medium">
                  {count}명
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 연령대별 분포 */}
        <div>
          <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <FaUsers className="mr-2" />
            연령대별 분포
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(ageStats).map(([ageGroup, count]) => (
              <div key={ageGroup} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{ageGroup}</span>
                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs font-medium">
                  {count}명
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 회원 목록 */}
        <div>
          <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <FaUsers className="mr-2" />
            회원 목록 ({totalCount}명)
          </h4>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {cellGroupMembers.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {member.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{member.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {member.gender === 'male' ? '남성' : '여성'} • {member.dateOfBirth?.split('-')[0]}년생
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                    {POSITION_MAP[member.position as keyof typeof POSITION_MAP] || '미지정'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
});

CellGroupDetailModal.displayName = 'CellGroupDetailModal';

export const MemberList: React.FC<MemberListProps> = React.memo(({
  members,
  onEdit,
  onDelete,
  isLoading = false,
  error = null
}) => {
  const [selectedCellGroup, setSelectedCellGroup] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGender, setFilterGender] = useState<'all' | 'male' | 'female'>('all');
  const [filterCellGroup, setFilterCellGroup] = useState<string>('all');

  // 소그룹 클릭 핸들러
  const handleCellGroupClick = useCallback((cellGroupId: string) => {
    setSelectedCellGroup(cellGroupId);
    setIsModalOpen(true);
  }, []);

  // 모달 닫기 핸들러
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCellGroup(null);
  }, []);

  // 필터링된 회원 데이터
  const filteredMembers = useMemo(() =>
    members.filter(member => {
      const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           member.phoneNumber.includes(searchTerm);
      
      const matchesGender = filterGender === 'all' || member.gender === filterGender;
      
      const matchesCellGroup = filterCellGroup === 'all' || member.cellGroup === filterCellGroup;
      
      return matchesSearch && matchesGender && matchesCellGroup;
    }),
    [members, searchTerm, filterGender, filterCellGroup]
  );

  // 고유한 구역 목록 추출
  const uniqueCellGroups = useMemo(() =>
    Array.from(new Set(members.map(member => member.cellGroup).filter(Boolean))),
    [members]
  );

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 오류 상태 처리
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
            <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">데이터 로딩 오류</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  if (filteredMembers.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-700">
          <FaUsers className="h-8 w-8 text-gray-400 dark:text-gray-500" />
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">검색 결과 없음</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {searchTerm || filterGender !== 'all' || filterCellGroup !== 'all'
            ? '검색 조건에 맞는 성도를 찾을 수 없습니다.'
            : '등록된 성도가 없습니다.'
          }
        </p>
        {(searchTerm || filterGender !== 'all' || filterCellGroup !== 'all') && (
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterGender('all');
              setFilterCellGroup('all');
            }}
            className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FaTimes className="mr-2 h-4 w-4" />
            필터 초기화
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
      {/* 검색 및 필터 영역 */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* 검색창 */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="성도 이름, 이메일, 전화번호로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
            />
          </div>
          
          {/* 필터 버튼들 */}
          <div className="flex flex-wrap gap-2">
            {/* 성별 필터 */}
            <select
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value as 'all' | 'male' | 'female')}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
            >
              <option value="all">전체 성별</option>
              <option value="male">남성</option>
              <option value="female">여성</option>
            </select>
            
            {/* 구역 필터 */}
            <select
              value={filterCellGroup}
              onChange={(e) => setFilterCellGroup(e.target.value)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
            >
              <option value="all">전체 구역</option>
              {uniqueCellGroups.map(group => (
                <option key={group} value={group}>{CELL_GROUP_MAP[group as keyof typeof CELL_GROUP_MAP] || group}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* 검색 결과 수 */}
        <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          총 {filteredMembers.length}명의 성도를 찾았습니다. (전체 {members.length}명)
        </div>
      </div>

      {/* Desktop Table */}
      <div className="overflow-x-auto hidden md:block">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-6 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">번호</th>
              <th scope="col" className="px-6 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">이름</th>
              <th scope="col" className="px-6 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">성별</th>
              <th scope="col" className="px-6 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">생년월일</th>
              <th scope="col" className="px-6 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">직분</th>
              <th scope="col" className="px-6 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">구역</th>
              <th scope="col" className="px-6 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">관리</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredMembers.map((member, index) => (
              <tr
                key={member.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                role="row"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400" role="cell">{index + 1}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center text-gray-900 dark:text-gray-200" role="cell">
                  <Link
                    to={`/members/${member.id}`}
                    className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    aria-label={`${member.name} 상세 정보`}
                  >
                    {member.name}
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400" role="cell">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    member.gender === 'male'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                      : 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300'
                  }`}>
                    {member.gender === 'male' ? '남성' : '여성'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400" role="cell">{member.dateOfBirth}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400" role="cell">
                  {member.position ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      {POSITION_MAP[member.position]}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center" role="cell">
                  {member.cellGroup ? (
                    <button
                      onClick={() => handleCellGroupClick(member.cellGroup!)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                      aria-label={`${CELL_GROUP_MAP[member.cellGroup]} 소그룹 상세 정보`}
                    >
                      {CELL_GROUP_MAP[member.cellGroup]}
                    </button>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium" role="cell">
                  <div className="flex items-center justify-center space-x-3">
                    {onEdit && (
                      <button
                        onClick={() => onEdit(member)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                        aria-label={`${member.name} 정보 수정`}
                      >
                        <FaEdit />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => onDelete(member.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                        aria-label={`${member.name} 정보 삭제`}
                      >
                        <FaTrashAlt />
                      </button>
                    )}
                    {!onEdit && !onDelete && (
                      <span className="text-gray-500 text-xs">읽기 전용</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
        {filteredMembers.map((member, index) => (
          <div key={member.id} className="p-4 bg-white dark:bg-gray-800 transition-colors duration-150">
            {/* 헤더 영역 */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {member.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    <Link
                      to={`/members/${member.id}`}
                      className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                      aria-label={`${member.name} 상세 정보`}
                    >
                      {member.name}
                    </Link>
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">#{index + 1}</p>
                </div>
              </div>
              <div className="flex gap-x-2">
                {onEdit && (
                  <button
                    onClick={() => onEdit(member)}
                    className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    aria-label={`${member.name} 정보 수정`}
                  >
                    <FaEdit size={16} />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => onDelete(member.id)}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-500"
                    aria-label={`${member.name} 정보 삭제`}
                  >
                    <FaTrashAlt size={16} />
                  </button>
                )}
                {!onEdit && !onDelete && (
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <span className="text-gray-500 text-xs">읽기 전용</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* 정보 영역 */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center space-x-2">
                <span className="text-gray-500 dark:text-gray-400 font-medium">성별:</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  member.gender === 'male'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300'
                }`}>
                  {member.gender === 'male' ? '남성' : '여성'}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-gray-500 dark:text-gray-400 font-medium">생년월일:</span>
                <span className="text-gray-700 dark:text-gray-300">{member.dateOfBirth}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-gray-500 dark:text-gray-400 font-medium">직분:</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {member.position ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      {POSITION_MAP[member.position]}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  )}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-gray-500 dark:text-gray-400 font-medium">구역:</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {member.cellGroup ? (
                    <button
                      onClick={() => handleCellGroupClick(member.cellGroup!)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                      aria-label={`${CELL_GROUP_MAP[member.cellGroup]} 소그룹 상세 정보`}
                    >
                      {CELL_GROUP_MAP[member.cellGroup]}
                    </button>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* 소그룹 상세 정보 모달 */}
      {selectedCellGroup && (
        <CellGroupDetailModal
          cellGroupId={selectedCellGroup}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          members={members}
        />
      )}
    </div>
  );
});

MemberList.displayName = 'MemberList';
